import * as React from 'react'
import {
  ComponentBlock,
  RichTextBlock,
  blockTypesRecord,
  isInstanceBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { Editor } from '@tiptap/react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { ToolbarToggleItem } from '@radix-ui/react-toolbar'
import { BaseHubInlineBlock } from '@basehub/tiptap-api-utils'
import { $toggleItem } from '..'
import { Component1Icon } from '@radix-ui/react-icons'
import { getId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'
import { BlockIcon } from '~/components/block-icon'
import { useRichTextStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text/store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

export const SelectInstanceBlock = ({
  editor,
  block,
  validComponents,
  path,
  containerElement,
  isActive,
}: {
  editor: Editor
  block: RichTextBlock
  validComponents: ComponentBlock[]
  path: string
  containerElement?: React.RefObject<HTMLElement | null>
  isActive: boolean
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const setRecentlyCreatedBlock = useRichTextStore(
    (s) => s.setRecentlyCreatedBlock
  )
  const [currentContainer, setCurrentContainer] =
    React.useState(containerElement)

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setCurrentContainer({ current: null })
    } else {
      setCurrentContainer(containerElement)
    }
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarToggleItem
          disabled={
            (validComponents.length === 0 ||
              editor.can().setMark(BaseHubInlineBlock.name) === false) &&
            !isActive
          }
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          className={$toggleItem({
            active: isActive,
          })}
          onClick={() => {
            if (isActive) {
              // remove mark
              editor.chain().focus().unsetBaseHubInlineBlock().run()
            }
          }}
          value="Component"
          aria-label="Component"
        >
          <Component1Icon />
        </ToolbarToggleItem>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        data-rt-editor-popover-id={block.id}
        align="end"
        side="top"
        sideOffset={8}
        container={currentContainer?.current}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        {validComponents.map((component: ComponentBlock) => (
          <DropdownMenuItem
            className="flex items-center space-x-2 text-xs leading-4 text-dark-dim py-2 hover:bg-dark-shade-hover w-full text-left px-2 rounded-md cursor-default"
            key={component.id}
            onClick={async () => {
              if (!replicache) return
              const { selection } = editor.state
              const { from, to } = selection
              const text = editor.state.doc.textBetween(from, to, ' ')
              // get enough words to make a title but with a max of 32 characters long but don't leave words withouth ending
              const title = text
                .split(' ')
                .reduce(
                  (acc, word) =>
                    acc.length + word.length < 32 ? `${acc} ${word}` : acc,
                  ''
                )
                .trim()

              const { newBlock: instance } =
                await replicache.mutate.createBlock({
                  data: {
                    id: getId(),
                    type: 'instance',
                    title,
                    typeOptions: {
                      targetBlockId: idToSourceBlockId(component),
                    },
                  },
                  parentInstanceChildTarget: block.instanceChildTarget,
                  position: { type: 'last' },
                  idSeed: getId(),
                  isoNow: new Date().toISOString(),
                  path: normalizeStringPath({
                    path,
                    edgeId: block.id,
                    format: 'string',
                    includeEdge: true,
                  }),
                })
              invariant(
                instance && isInstanceBlock(instance),
                'instance is undefined'
              )
              setRecentlyCreatedBlock(instance)
              editor.chain().setBaseHubInlineBlock(instance.id).run()
            }}
          >
            <BlockIcon block={{ type: blockTypesRecord.instance }} />
            <span>{component.title || `Untitled`}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
